import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import NavigationHeader from './components/navigationHeader';
import PageContainer from './components/pageContainer';
import PageLayout from './components/pageLayout';
import PageBody from './components/pageBody';
import PageText from './components/pageText';
import PageFooter from './components/pageFooter';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import './CheckoutPage.css';
import { API_URL } from '../config';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';


const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#333',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#e74c3c' },
  },
};

const AppleGooglePayButton = ({ orderTotal, shipping, onSuccess }) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);

  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'eggtarty studio', amount: Math.round(orderTotal * 100) },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) setPaymentRequest(pr);
    });

    pr.on('paymentmethod', async (e) => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: Math.round(orderTotal * 100), shipping }),
        });
        const { clientSecret, error: serverError } = await res.json();
        if (serverError) { e.complete('fail'); return; }

        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: e.paymentMethod.id },
          { handleActions: false }
        );

        if (error) { e.complete('fail'); return; }

        e.complete('success');
        if (paymentIntent.status === 'requires_action') {
          const { error: actionError } = await stripe.confirmCardPayment(clientSecret);
          if (!actionError) onSuccess();
        } else {
          onSuccess();
        }
      } catch { e.complete('fail'); }
    });
  }, [stripe, orderTotal, shipping, onSuccess]);

  if (!paymentRequest) return null;

  return (
    <div className="apple-pay-section">
      <PaymentRequestButtonElement
        options={{ paymentRequest, style: { paymentRequestButton: { height: '48px' } } }}
      />
      <div className="checkout-divider"><span>or pay with card</span></div>
    </div>
  );
};

const StripeCheckoutForm = ({ cartTotal, shipping, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    // free order — skip payment
    if (cartTotal <= 0) { onSuccess(); return; }

    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Math.round(cartTotal * 100), shipping }),
      });
      const { clientSecret, error: serverError } = await response.json();

      if (serverError) {
        setError(serverError);
        setProcessing(false);
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        setError(result.error.message);
      } else if (result.paymentIntent.status === 'succeeded') {
        onSuccess();
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <h3 className="checkout-section-title">pay with card</h3>
      <p className="checkout-powered-by">credit card payments powered by Stripe</p>
      <div className="card-element-wrapper">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      {error && <p className="checkout-error">{error}</p>}
      <button type="submit" disabled={!stripe || processing} className="checkout-pay-btn">
        {processing ? 'processing...' : `pay $${cartTotal.toFixed(2)}`}
      </button>
    </form>
  );
};

const PayPalCheckout = ({ cartTotal, cartItems, shipping, shippingCost, onSuccess }) => {
  const [error, setError] = useState(null);

  return (
    <div className="paypal-section">
      <h3 className="checkout-section-title">or pay with paypal</h3>
      <PayPalButtons
        style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
        createOrder={async () => {
          const response = await fetch(`${API_URL}/api/paypal/create-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartTotal, cartItems, shipping, shippingCost }),
          });
          const { orderID } = await response.json();
          return orderID;
        }}
        onApprove={async (data) => {
          try {
            const response = await fetch(`${API_URL}/api/paypal/capture-order`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orderID: data.orderID }),
            });
            const result = await response.json();
            if (result.status === 'COMPLETED') {
              onSuccess();
            }
          } catch (err) {
            setError('PayPal payment failed. Please try again.');
          }
        }}
        onError={() => {
          setError('Something went wrong with PayPal. Please try again.');
        }}
      />
      {error && <p className="checkout-error">{error}</p>}
    </div>
  );
};

const SHIPPING_COST = 5;
const FREE_SHIPPING_THRESHOLD = 50;

const CheckoutContent = () => {
  const { cartItems, cartTotal, clearCart } = useCart();
  const [orderComplete, setOrderComplete] = useState(false);
  const [shipping, setShipping] = useState({
    fullName: '', address1: '', address2: '', city: '', state: '', zip: '',
  });
  const [receiptEmail, setReceiptEmail] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  const freeShipping = cartTotal >= FREE_SHIPPING_THRESHOLD;
  const shippingCost = freeShipping ? 0 : SHIPPING_COST;
  const orderTotal = cartTotal + shippingCost;

  const shippingComplete = shipping.fullName && shipping.address1 && shipping.city && shipping.state && shipping.zip && customerEmail;

  const updateShipping = (field) => (e) => setShipping({ ...shipping, [field]: e.target.value });

  const handleSuccess = async (email) => {
    // Decrement nail inventory for purchased nail sets
    const nailItems = cartItems.filter((i) => i.category === 'nails');
    const totalNailQty = nailItems.reduce((sum, i) => sum + (i.quantity || 1), 0);
    const nailDecrements = totalNailQty > 0
      ? [fetch(`${API_URL}/api/inventory/decrement-nail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ count: totalNailQty }),
        }).catch(() => {})]
      : [];

    // Submit any pending commissions now that payment succeeded
    const pendingNailCommissions = cartItems.filter((i) => i.pendingCommission);
    const pendingArtCommissions = cartItems.filter((i) => i.pendingArtCommission);
    const commissionPromises = [
      ...pendingNailCommissions.map((i) =>
        fetch(`${API_URL}/api/commissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(i.pendingCommission),
        }).catch(() => {})
      ),
      ...pendingArtCommissions.map((i) =>
        fetch(`${API_URL}/api/art-commissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(i.pendingArtCommission),
        }).catch(() => {})
      ),
    ];

    // save order + send receipt + submit commissions + decrement inventory — best effort
    try {
      await Promise.all([
        ...nailDecrements,
        ...commissionPromises,
        fetch(`${API_URL}/api/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: shipping.fullName,
            email,
            items: cartItems.map(({ id, productId, title, price, quantity, imagePath, options }) => ({ id, productId, title, price, quantity, imagePath, options })),
            orderTotal,
            shippingCost,
            shipping,
          }),
        }),
        fetch(`${API_URL}/api/orders/receipt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: shipping.fullName,
            email,
            items: cartItems.map(({ id, productId, title, price, quantity, imagePath, options }) => ({ id, productId, title, price, quantity, imagePath, options })),
            orderTotal,
            shippingCost,
            shipping,
          }),
        }),
      ]);
    } catch { /* silent */ }
    setReceiptEmail(email);
    clearCart();
    setOrderComplete(true);
  };

  if (orderComplete) {
    return (
      <div className="checkout-success">
        <p>thank you for your order ✧</p>
        {receiptEmail && (
          <p className="checkout-success-email">a receipt has been sent to {receiptEmail}</p>
        )}
        <Link to="/" className="cart-continue-link">back to home</Link>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="cart-empty">
        <p>nothing to check out</p>
        <Link to="/shop" className="cart-continue-link">continue shopping</Link>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <h2 className="checkout-title">checkout</h2>

      <div className="checkout-order-summary">
        {cartItems.map((item) => (
          <div key={item.id} className="checkout-item">
            <span>{item.title} × {item.quantity}</span>
            <span>${(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="checkout-item checkout-item-subtotal">
          <span>subtotal</span>
          <span>${cartTotal.toFixed(2)}</span>
        </div>
        <div className="checkout-item">
          <span>shipping</span>
          <span>{freeShipping ? <span className="checkout-free-shipping">free ✧</span> : `$${shippingCost.toFixed(2)}`}</span>
        </div>
        {!freeShipping && (
          <p className="checkout-shipping-hint">free shipping on orders over ${FREE_SHIPPING_THRESHOLD}</p>
        )}
        <div className="checkout-item checkout-item-total">
          <span>total</span>
          <span>${orderTotal.toFixed(2)} USD</span>
        </div>
      </div>

      <div className="checkout-shipping">
        <h3 className="checkout-section-title">shipping address</h3>
        <input type="text" placeholder="full name" value={shipping.fullName} onChange={updateShipping('fullName')} className="checkout-input" />
        <input type="email" placeholder="email for receipt *" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="checkout-input" required />
        <input type="text" placeholder="address line 1" value={shipping.address1} onChange={updateShipping('address1')} className="checkout-input" />
        <input type="text" placeholder="address line 2 (optional)" value={shipping.address2} onChange={updateShipping('address2')} className="checkout-input" />
        <div className="checkout-input-row">
          <input type="text" placeholder="city" value={shipping.city} onChange={updateShipping('city')} className="checkout-input" />
          <input type="text" placeholder="state" value={shipping.state} onChange={updateShipping('state')} className="checkout-input checkout-input-sm" />
          <input type="text" placeholder="zip" value={shipping.zip} onChange={updateShipping('zip')} className="checkout-input checkout-input-sm" />
        </div>
        {!shippingComplete && <p className="checkout-hint">please fill in shipping info to continue</p>}
      </div>

      {shippingComplete && (
        <>
          <Elements stripe={stripePromise}>
            <AppleGooglePayButton orderTotal={orderTotal} shipping={shipping} onSuccess={() => handleSuccess(customerEmail)} />
            <StripeCheckoutForm cartTotal={orderTotal} shipping={shipping} onSuccess={() => handleSuccess(customerEmail)} />
          </Elements>

          {PAYPAL_CLIENT_ID && (
            <>
              <div className="checkout-divider">
                <span>or</span>
              </div>
              <PayPalCheckout cartTotal={orderTotal} cartItems={cartItems} shipping={shipping} shippingCost={shippingCost} onSuccess={() => handleSuccess(customerEmail)} />
            </>
          )}
        </>
      )}

      <Link to="/cart" className="checkout-back-link">← back to cart</Link>
    </div>
  );
};

const CheckoutInner = () => (
  <PageLayout>
    <PageContainer>
      <PageBody>
        <NavigationHeader />
        <PageText>
          <CheckoutContent />
        </PageText>
        <PageFooter />
      </PageBody>
    </PageContainer>
  </PageLayout>
);

export const CheckoutPage = () => {
  if (!PAYPAL_CLIENT_ID) return <CheckoutInner />;
  return (
    <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD' }}>
      <CheckoutInner />
    </PayPalScriptProvider>
  );
};
